import trapInspectionService from '@/services/trapInspectionService';
import moment from 'moment';

export default  {
    namespaced: true,
    state: {
        networkError: false,        
        traps: [],
        trapInspections: [],
        defaultFromDate: moment().add(-30, 'days').format('YYYY-MM-DDT00:00:00'),
        defaultToDate: moment().format('YYYY-MM-DDT23:59:59'),
        unsyncedTotal: 0
    },
    getters: {
        getUnsyncedTotal(state) {
            return state.unsyncedTotal;
        },
        getTraps(state) {
            return (plantID, divisionID, locationID, filteredLocationsByProductCategory) => {
                return state.traps.filter(t => t.PlantID === plantID && (!divisionID || t.DivisionID === divisionID) && (!locationID || t.LocationID === locationID) && (filteredLocationsByProductCategory.some(f => f.LocationID === t.LocationID)));
            }
        },
        getTrap(state) {
            return (trapID) => {
                return state.traps.find(t => t.TrapID === trapID);
            }
        },
        getInspections(state) {
            return (trapID) => {
                if(state.trapInspections) {
                    return state.trapInspections.filter(t => t.TrapID === trapID && !t.IsDeleted);
                }

                return [];
            }
        },
        getInspectionsByID(state) {
            return (trapInspectionID) => {                
                return state.trapInspections.filter(t => t.TrapInspectionID === trapInspectionID);
            }
        }
    },
    mutations: {
        setUnsyncedTotal(state, value) {
            state.unsyncedTotal = value;
        },
        setNetworkError(state, value){
            state.networkError = value;
        },
        updateTraps(state, traps) {
            state.traps = traps;
        },
        updateTrap(state, trap) {
            let result = state.traps.find(t => t.TrapID === trap.TrapID);
            
            if (result) {
                result = trap;
            }
        },
        updateTrapInspections(state, trapInspections) {
            state.trapInspections = trapInspections;
        }
    },
    actions: {
        async handleOffline({ commit }, options){
            try {
                await options.action();
                commit("setNetworkError", false);
            } catch(e){
                commit("setNetworkError", true);
            }
        },
        async loadTrapsForLocationByTrap({ commit, dispatch }, trapID) {
            if (!trapID) {
                commit('updateTraps', []);
                return;
            }

            // Try to load from the server and if we go offline then just continue and try to load from indexdb.
            await dispatch("handleOffline", {
                async action(){
                    await trapInspectionService.fetchAndStoreTrapsForLocationByTrapID(trapID);
                }
            });

            // Load from indexdb
            let traps = await trapInspectionService.getTrapsForLocationByTrapID(trapID);
            commit('updateTraps', traps);
        },
        async loadTraps({ commit, dispatch }, plantID) {
            if (!plantID) {
                commit('updateTraps', []);
                return;
            }

            // Try to load from the server and if we go offline then just continue and try to load from indexdb.
            await dispatch("handleOffline", {
                async action(){
                    await trapInspectionService.fetchAndStoreTraps(plantID);
                }
            });

            // Load from indexdb
            let traps = await trapInspectionService.getTrapsByPlant(plantID);
            commit('updateTraps', traps);
        },
        async loadTrapInspectionsForPlant({ commit, dispatch, state }, { plantID }) {
            // Try to load from the server for offline use
            await dispatch("handleOffline", {
                async action(){                    
                    await trapInspectionService.fetchAndStoreTrapInspections(plantID, state.defaultFromDate, state.defaultToDate);
                }
            });

            let inspections = await trapInspectionService.getTrapInspections();
            commit('updateTrapInspections', inspections);

            await dispatch("updateUnsyncedTotal");
        },
        async loadTrapInspectionsForTrap({ commit, dispatch, state }, trapID) {
            let fromDate = state.defaultFromDate;
            let toDate = state.defaultToDate;

            // Try to load from the server
            await dispatch("handleOffline", {
                async action(){
                    await trapInspectionService.fetchAndStoreTrapInspectionsByTrap(trapID, fromDate, toDate);
                }
            });

            let inspections = await trapInspectionService.getTrapInspections();
            commit('updateTrapInspections', inspections);
        },
        async loadMoreTrapInspectionsForTrap({ commit, dispatch, state }, trapID) {
            let fromDate = state.defaultFromDate;
            let toDate = state.defaultToDate;

            let trap = state.traps.find(t => t.TrapID === trapID);

            if (trap) {                
                trap.toDate = moment(trap.fromDate ? trap.fromDate : fromDate).add(-1, 'seconds').format('YYYY-MM-DDTHH:mm:ss');
                trap.fromDate = moment(trap.toDate).add(-30, 'days').format('YYYY-MM-DDT00:00:00');

                commit('updateTrap', trap);

                fromDate = trap.fromDate;
                toDate = trap.toDate;
            }

            // Try to load from the server
            await dispatch("handleOffline", {
                async action(){
                    await trapInspectionService.fetchAndStoreTrapInspectionsByTrap(trapID, fromDate, toDate);
                }
            });

            let inspections = await trapInspectionService.getTrapInspections();
            commit('updateTrapInspections', inspections);
        },
        async updateUnsyncedTotal({ commit }) {
            const items = await trapInspectionService.getUnsetTrapInspections();            
            commit('setUnsyncedTotal', items.length);
        },
        async syncUnsyncedData({ dispatch, commit }) {
            await dispatch("handleOffline", {
                async action() {                    
                    await trapInspectionService.postUnSyncedData();                    
                }
            });

            let inspections = await trapInspectionService.getTrapInspections();
            commit('updateTrapInspections', inspections);
            
            await dispatch('updateUnsyncedTotal');
        },
        async saveTrapInspection({ dispatch }, trapInspection) {
            let items = [];

            // This is a newly created item, then lets give it negative number
            if (!trapInspection.TrapInspectionID) {
                trapInspection.TrapInspectionID = await trapInspectionService.getNextTrapInspectionID();
            }

            trapInspection.Details.filter(td => td.TrapInspectionDetailID > 0 || (td.BugCount != null && td.BugCount !== '') || (td.Notes != null && td.Notes !== '')).map(t => items.push({
                TrapID: t.TrapModel.TrapID,
                TrapInspectionID: trapInspection.TrapInspectionID,
                TrapInspectionDetailID: trapInspection.TrapInspectionDetailID || 0,
                LocationID : trapInspection.LocationID,
                LocationName: trapInspection.LocationName,
                InspectionDate: trapInspection.InspectionDate,
                InspectionNotes: trapInspection.InspectionNotes,
                TrapModel: t.TrapModel,
                BugCount: t.BugCount,
                Notes: t.Notes,
                Latitude: t.Latitude,
                Longitude: t.Longitude,
                EditTimeStamp: moment().format('YYYY-MM-DDTHH:mm:ss'),
                Deleted: trapInspection.Deleted
            }));

            // Save locally and try to submit to the server
            await trapInspectionService.addEditTrapInspections(items);
            await dispatch("syncUnsyncedData");
        }
    }
}